/**
 * Reference Data Management Page
 * Manages all reference data tables
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiClient';
import { formatNumber, formatRatePct } from '../utils/formatters';
import { usePermissions } from '../contexts/PermissionContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTenant } from '../contexts/TenantContext';
import { MODULES, PERMISSION_LEVELS } from '../contexts/PermissionConstants';
import { a11yProps } from '../components/common/TabPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DataTable from '../components/common/DataTable';
import useConfirmDialog from '../hooks/useConfirmDialog';
import usePagination from '../hooks/usePagination';
import useTableSort from '../hooks/useTableSort';
import logger from '../utils/logger';

// API base path per reference-data type. Single source of truth shared by the
// create/update (onSubmit), delete, and reactivate handlers.
const REFDATA_ENDPOINTS = {
  countries: '/refdata/countries',
  frequencies: '/refdata/frequencies',
  investmentTypes: '/refdata/investment-types',
  programs: '/refdata/programs',
  interestRates: '/refdata/interest-rates',
  fxRates: '/refdata/fx-rates',
  placementFees: '/refdata/placement-fees',
  collateralTypes: '/refdata/collateral-types',
  narrations: '/refdata/narrations',
  whtRules: '/refdata/wht-rules',
  commissionRates: '/refdata/commission-rates',
};

function ReferenceDataManagement() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { isFeatureEnabled } = useTenant();
  const notify = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [interestRateCountryTab, setInterestRateCountryTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dataType, setDataType] = useState('countries');
  const [submitError, setSubmitError] = useState(null);
  const confirm = useConfirmDialog();

  // Per-tab "show inactive" flags, keyed by refdata type (e.g. showInactive.countries).
  // Falsy = active-only (default). When on, that type is fetched with active_only=false.
  const [showInactive, setShowInactive] = useState({});
  // Per-tab selection of inactive row ids for bulk reactivate, keyed by type.
  const [selected, setSelected] = useState({});

  const canWrite = hasPermission(MODULES.REFERENCE_DATA, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.REFERENCE_DATA, PERMISSION_LEVELS.ADMIN);

  const countriesPagination = usePagination({ defaultRowsPerPage: 10 });
  const frequenciesPagination = usePagination({ defaultRowsPerPage: 10 });
  const investmentTypesPagination = usePagination({ defaultRowsPerPage: 10 });
  const programsPagination = usePagination({ defaultRowsPerPage: 10 });
  const fxRatesPagination = usePagination({ defaultRowsPerPage: 10 });
  const placementFeesPagination = usePagination({ defaultRowsPerPage: 10 });
  const collateralTypesPagination = usePagination({ defaultRowsPerPage: 10 });
  const narrationsPagination = usePagination({ defaultRowsPerPage: 10 });
  const whtRulesPagination = usePagination({ defaultRowsPerPage: 10 });
  const commissionRatesPagination = usePagination({ defaultRowsPerPage: 10 });

  const countriesSort = useTableSort({ onSortChange: countriesPagination.resetPage });
  const frequenciesSort = useTableSort({ onSortChange: frequenciesPagination.resetPage });
  const investmentTypesSort = useTableSort({ onSortChange: investmentTypesPagination.resetPage });
  const programsSort = useTableSort({ onSortChange: programsPagination.resetPage });
  const fxRatesSort = useTableSort({ onSortChange: fxRatesPagination.resetPage });
  const placementFeesSort = useTableSort({ onSortChange: placementFeesPagination.resetPage });
  const collateralTypesSort = useTableSort({ onSortChange: collateralTypesPagination.resetPage });
  const narrationsSort = useTableSort({ onSortChange: narrationsPagination.resetPage });
  const whtRulesSort = useTableSort({ onSortChange: whtRulesPagination.resetPage });
  const commissionRatesSort = useTableSort({ onSortChange: commissionRatesPagination.resetPage });

  // Impact assessment dialog state
  const [impactDialog, setImpactDialog] = useState({
    open: false,
    rateType: '',
    countryId: '',
    newRate: '',
    effectiveDate: '',
    benchmarkRateId: '',
  });

  // Data states
  const [countries, setCountries] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [investmentTypes, setInvestmentTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [issuersList, setIssuersList] = useState([]);
  const [interestRates, setInterestRates] = useState([]);
  const [fxRates, setFxRates] = useState([]);
  const [placementFees, setPlacementFees] = useState([]);
  const [collateralTypes, setCollateralTypes] = useState([]);
  const [narrations, setNarrations] = useState([]);
  const [whtRules, setWhtRules] = useState([]);
  const [commissionRates, setCommissionRates] = useState([]);
  const [brokersList, setBrokersList] = useState([]);
  const [investorsList, setInvestorsList] = useState([]);

  const { control, handleSubmit, reset, setValue, watch } = useForm();

  // Watch form fields for Interest Rates form inter-field dependencies
  const watchedCountryId = watch('country_id');
  const watchedInterestRateType = watch('interest_rate_type');

  useEffect(() => {
    loadAllData();
  }, []);

  // Reset country sub-tab if selected index goes out of bounds after data reload
  useEffect(() => {
    const uniqueCountryIds = new Set(interestRates.map(r => r.country_id).filter(Boolean));
    if (interestRateCountryTab > uniqueCountryIds.size) {
      setInterestRateCountryTab(0);
    }
  }, [interestRates, interestRateCountryTab]);

  // Auto-populate Prime Rate Institution when country and rate type are selected
  useEffect(() => {
    if (dataType === 'interestRates' && watchedCountryId && watchedInterestRateType) {
      // Find the most recent rate with this country and type to get the institution
      const matchingRates = interestRates
        .filter(r => r.country_id === watchedCountryId && r.interest_rate_type === watchedInterestRateType)
        .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));

      if (matchingRates.length > 0 && matchingRates[0].prime_rate_institution) {
        setValue('prime_rate_institution', matchingRates[0].prime_rate_institution);
      }
    }
  }, [dataType, watchedCountryId, watchedInterestRateType, interestRates, setValue]);

  // Maps each refdata type to its API path + state setter, so a single type can be
  // (re)fetched independently with its own active_only flag. The state setters are
  // stable across renders, so this object is built once.
  const refdataSetters = useMemo(() => ({
    countries: setCountries,
    frequencies: setFrequencies,
    investmentTypes: setInvestmentTypes,
    programs: setPrograms,
    interestRates: setInterestRates,
    fxRates: setFxRates,
    placementFees: setPlacementFees,
    collateralTypes: setCollateralTypes,
    narrations: setNarrations,
    whtRules: setWhtRules,
    commissionRates: setCommissionRates,
  }), []);

  // Fetch a single refdata type. When showInactiveFlag is true, includes inactive
  // rows (active_only=false); otherwise active-only (the default backend behaviour).
  const loadRefdataType = async (type, showInactiveFlag) => {
    const setter = refdataSetters[type];
    if (!setter) return;
    try {
      const res = await apiGet(`${REFDATA_ENDPOINTS[type]}?active_only=${!showInactiveFlag}`);
      setter(res?.data || []);
    } catch (err) {
      logger.error('Error loading reference data', { type, message: err.message, status: err.response?.status });
      setter([]);
    }
  };

  // Reload one type using its current per-tab show-inactive flag.
  const reloadType = (type) => loadRefdataType(type, !!showInactive[type]);

  const loadAllData = async () => {
    // Each refdata type respects its own show-inactive flag; on first mount all
    // flags are falsy, so this loads active-only across the board (unchanged).
    const refdataLoads = Object.keys(refdataSetters).map((type) =>
      loadRefdataType(type, !!showInactive[type])
    );

    // Supporting stakeholder lists for the program/commission forms (no active_only param).
    const stakeholderLoads = (async () => {
      try {
        const [brokersListData, investorsListData, issuersData] = await Promise.all([
          apiGet('/stakeholders/brokers').catch(() => []),
          apiGet('/stakeholders/investors').catch(() => []),
          apiGet('/stakeholders/issuers').catch(() => []),
        ]);
        setBrokersList(brokersListData?.data || []);
        setInvestorsList(investorsListData?.data || []);
        setIssuersList(issuersData?.data || []);
      } catch (err) {
        logger.error('Error loading stakeholder lists', { message: err.message, status: err.response?.status });
      }
    })();

    await Promise.all([...refdataLoads, stakeholderLoads]);
  };

  // Helper to format date for input[type="date"] (requires YYYY-MM-DD)
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const handleOpenDialog = (type, item = null) => {
    setDataType(type);
    setEditingItem(item);
    if (item) {
      Object.keys(item).forEach(key => {
        // Format date fields for input[type="date"]
        if (key === 'effective_date' || key.endsWith('_date') || key === 'effective_from' || key === 'effective_to') {
          setValue(key, formatDateForInput(item[key]));
        } else {
          setValue(key, item[key]);
        }
      });
      // Split currency_pair into from_currency and to_currency for FX rates
      if (type === 'fxRates' && item.currency_pair) {
        const [from, to] = item.currency_pair.split('/');
        setValue('from_currency', from || '');
        setValue('to_currency', to || '');
      }
      // Map the program's investment_types array of objects to an ids array
      if (type === 'programs') {
        const ids = Array.isArray(item.investment_types) ? item.investment_types.map((t) => t.id) : [];
        setValue('investment_type_ids', ids);
      }
    } else {
      reset();
    }
    setSubmitError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setSubmitError(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      const endpoint = REFDATA_ENDPOINTS[dataType];
      let savedId = null;

      // Combine from_currency and to_currency into currency_pair for FX rates
      let submitData = { ...data };
      if (dataType === 'fxRates' && data.from_currency && data.to_currency) {
        submitData.currency_pair = `${data.from_currency}/${data.to_currency}`;
        delete submitData.from_currency;
        delete submitData.to_currency;
      }

      if (editingItem) {
        await apiPut(`${endpoint}/${editingItem.id}`, submitData);
        savedId = editingItem.id;
      } else {
        const response = await apiPost(endpoint, submitData);
        savedId = response?.data?.id;
      }

      await loadAllData();
      handleCloseDialog();

      // Show impact assessment prompt for interest rates
      if (dataType === 'interestRates' && data.interest_rate_type && data.country_id) {
        setImpactDialog({
          open: true,
          rateType: data.interest_rate_type,
          countryId: data.country_id,
          newRate: data.effective_interest_rate,
          effectiveDate: data.effective_date,
          benchmarkRateId: savedId || '',
        });
      }
    } catch (err) {
      logger.error('Error saving reference data', { message: err.message, status: err.response?.status });
      const apiMessage = err.response?.data?.error?.message;
      setSubmitError(apiMessage || err.message || 'Failed to save reference data');
    }
  };

  const handleDelete = (type, id) => {
    confirm.show({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await apiDelete(`${REFDATA_ENDPOINTS[type]}/${id}`);
          await reloadType(type);
        } catch (err) {
          logger.error('Error deleting reference data', { message: err.message, status: err.response?.status });
          notify.error('Failed to delete item: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  // Build the PUT body that sets a row's active flag. Reuses the existing update
  // endpoint. Most types accept the row spread directly; programs need their
  // investment_type_ids array rebuilt from the row's investment_types objects, or the
  // update validation rejects it (empty type-id list -> 400).
  const buildSetActiveBody = (type, row, active) => {
    if (type === 'programs') {
      return {
        ...row,
        investment_type_ids: Array.isArray(row.investment_types)
          ? row.investment_types.map((t) => t.id)
          : [],
        active,
      };
    }
    return { ...row, active };
  };

  const handleReactivate = (type, row) => {
    confirm.show({
      title: 'Reactivate Item',
      message: 'Are you sure you want to reactivate this item?',
      confirmText: 'Reactivate',
      onConfirm: async () => {
        try {
          await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, true));
          await reloadType(type);
          notify.success('Item reactivated');
        } catch (err) {
          logger.error('Error reactivating reference data', { message: err.message, status: err.response?.status });
          notify.error('Failed to reactivate item: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  // Bulk activate (active=true) or deactivate (active=false) the selected rows of a tab.
  // Reuses the existing update endpoint per row; only operates on rows whose state
  // would actually change, so selecting a mix is safe.
  const handleBulkSetActive = (type, rows, active) => {
    const ids = selected[type] || [];
    const targets = (rows || []).filter((r) => ids.includes(r.id) && r.active !== active);
    if (targets.length === 0) {
      notify.info(`No selected items to ${active ? 'activate' : 'deactivate'}`);
      return;
    }

    const verb = active ? 'activate' : 'deactivate';
    confirm.show({
      title: active ? 'Activate Items' : 'Deactivate Items',
      message: `Are you sure you want to ${verb} ${targets.length} item${targets.length === 1 ? '' : 's'}?`,
      confirmText: active ? 'Activate' : 'Deactivate',
      onConfirm: async () => {
        let failures = 0;
        let firstError = null;
        for (const row of targets) {
          try {
            await apiPut(`${REFDATA_ENDPOINTS[type]}/${row.id}`, buildSetActiveBody(type, row, active));
          } catch (err) {
            failures += 1;
            if (!firstError) firstError = err.response?.data?.error?.message || err.response?.data?.message || err.message;
            logger.error(`Error ${verb} reference data (bulk)`, { id: row.id, message: err.message, status: err.response?.status });
          }
        }
        setSelected((prev) => ({ ...prev, [type]: [] }));
        await reloadType(type);
        const succeeded = targets.length - failures;
        if (failures > 0) {
          notify.warning(`${succeeded} item${succeeded === 1 ? '' : 's'} ${verb}d, ${failures} failed${firstError ? `: ${firstError}` : ''}`);
        } else {
          notify.success(`${succeeded} item${succeeded === 1 ? '' : 's'} ${verb}d`);
        }
      },
    });
  };

  // Toggle a tab's "show inactive" flag: clears that tab's selection, resets its
  // pagination page (row set changes), and reloads the type with the new flag.
  const handleToggleShowInactive = (type, pagination) => {
    const next = !showInactive[type];
    setShowInactive((prev) => ({ ...prev, [type]: next }));
    setSelected((prev) => ({ ...prev, [type]: [] }));
    pagination?.resetPage?.();
    loadRefdataType(type, next);
  };

  // Toggle a single row's selection within a tab.
  const toggleSelected = (type, id) => {
    setSelected((prev) => {
      const current = prev[type] || [];
      const exists = current.includes(id);
      return { ...prev, [type]: exists ? current.filter((x) => x !== id) : [...current, id] };
    });
  };

  const buildTableColumns = (fieldColumns, type) => {
    const selectedIds = selected[type] || [];
    return [
      // Selection checkbox column — always present (stable layout) for admins, so the
      // bulk activate/deactivate selector is available regardless of the active filter.
      ...(canAdmin ? [{
        key: '_select',
        header: '',
        align: 'center',
        sx: { width: 44, px: 0 },
        render: (row) => (
          <Checkbox
            size="small"
            sx={{ p: 0.5 }}
            checked={selectedIds.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleSelected(type, row.id)}
          />
        ),
      }] : []),
      ...fieldColumns.map((col) => ({
        key: col.field,
        header: col.header,
        sortable: true,
        getValue: (row) => row[col.field],
        render: col.render ? (row) => col.render(row[col.field], row) : undefined,
      })),
      {
        key: '_actions',
        header: 'Actions',
        align: 'center',
        render: (row) => (
          <>
            {canWrite && (
              <IconButton size="small" onClick={() => handleOpenDialog(type, row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {canAdmin && (
              row.active === false ? (
                <IconButton size="small" color="success" title="Reactivate" onClick={() => handleReactivate(type, row)}>
                  <RestoreIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton size="small" color="error" title="Delete" onClick={() => handleDelete(type, row.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )
            )}
          </>
        ),
      },
    ];
  };

  const formatProgramAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const renderActiveChip = (active) => (
    <Chip
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      size="small"
    />
  );

  // Float inactive rows to the top when that tab's "show inactive" is on, so they're
  // easy to spot for review/restore; otherwise return data untouched. Stable within
  // each group, and overridden once the user applies an explicit column sort.
  const orderInactiveFirst = (type, data) => {
    if (!showInactive[type] || !Array.isArray(data)) return data;
    const inactive = data.filter((r) => r.active === false);
    const active = data.filter((r) => r.active !== false);
    return inactive.length ? [...inactive, ...active] : data;
  };

  // Shared tab header. Left: bulk Activate/Deactivate actions (shown when rows are
  // selected). Right: "Show inactive" toggle then the tab's own "Add" action.
  // `data` is the tab's current row array (used to resolve selected ids for bulk ops).
  const renderTabHeader = (type, addButton, pagination, data) => {
    const selectedCount = (selected[type] || []).length;
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {canAdmin && selectedCount > 0 && (
          <>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => handleBulkSetActive(type, data, true)}
            >
              Activate ({selectedCount})
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => handleBulkSetActive(type, data, false)}
            >
              Deactivate ({selectedCount})
            </Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!showInactive[type]}
              onChange={() => handleToggleShowInactive(type, pagination)}
            />
          }
          label="Show inactive"
        />
        {addButton}
      </Box>
    );
  };

  // Sub-feature tab filtering
  const allRefTabs = useMemo(() => [
    { feature: 'countries', label: 'Countries' },
    { feature: 'frequencies', label: 'Frequencies' },
    { feature: 'investment_types', label: 'Investment Types' },
    { feature: 'programs', label: 'Programs' },
    { feature: 'interest_rates', label: 'Interest Rates' },
    { feature: 'fx_rates', label: 'FX Rates' },
    { feature: 'placement_fees', label: 'Placement Fees' },
    { feature: 'collateral_types', label: 'Collateral Types' },
    { feature: 'narrations', label: 'Narrations' },
    { feature: 'wht_rules', label: 'WHT Rules' },
    { feature: 'commission_rates', label: 'Commission Rates' },
  ], []);

  const visibleTabs = useMemo(() =>
    allRefTabs.filter(t => isFeatureEnabled('reference_data', t.feature)),
    [allRefTabs, isFeatureEnabled]
  );

  // Clamp tab value if tabs were removed
  const safeTabValue = visibleTabs.length > 0 ? Math.min(tabValue, visibleTabs.length - 1) : 0;
  const activeFeature = visibleTabs[safeTabValue]?.feature;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Reference Data Management</Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={safeTabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map(t => (
            <Tab key={t.feature} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Countries */}
      {activeFeature === 'countries' && (
      <Box>
        {renderTabHeader('countries',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('countries')}
            >
              Add Country
            </Button>
          ), countriesPagination, countries)}
        <DataTable
          columns={buildTableColumns([
            { field: 'country', header: 'Country' },
            { field: 'country_code', header: 'Code' },
            { field: 'currency_code', header: 'Currency' },
            { field: 'currency_symbol', header: 'Symbol' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'countries')}
          data={orderInactiveFirst('countries', countries)}
          pagination={countriesPagination}
          sort={countriesSort}
          emptyMessage="No countries found"
        />
      </Box>
      )}

      {/* Frequencies */}
      {activeFeature === 'frequencies' && (
      <Box>
        {renderTabHeader('frequencies',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('frequencies')}
            >
              Add Frequency
            </Button>
          ), frequenciesPagination, frequencies)}
        <DataTable
          columns={buildTableColumns([
            { field: 'frequency', header: 'Frequency' },
            { field: 'months_value', header: 'Months' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'frequencies')}
          data={orderInactiveFirst('frequencies', frequencies)}
          pagination={frequenciesPagination}
          sort={frequenciesSort}
          emptyMessage="No frequencies found"
        />
      </Box>
      )}

      {/* Investment Types */}
      {activeFeature === 'investment_types' && (
      <Box>
        {renderTabHeader('investmentTypes',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('investmentTypes')}
            >
              Add Investment Type
            </Button>
          ), investmentTypesPagination, investmentTypes)}
        <DataTable
          columns={buildTableColumns([
            { field: 'reference', header: 'Reference' },
            { field: 'type', header: 'Type' },
            { field: 'category', header: 'Category' },
            { field: 'cr_control_account', header: 'Control Account' },
            { field: 'dr_expense_account', header: 'Expense Account' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'investmentTypes')}
          data={orderInactiveFirst('investmentTypes', investmentTypes)}
          pagination={investmentTypesPagination}
          sort={investmentTypesSort}
          emptyMessage="No investment types found"
        />
      </Box>
      )}

      {/* Programs (capped MTN programs) */}
      {activeFeature === 'programs' && (
      <Box>
        {renderTabHeader('programs',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('programs')}
            >
              Add Program
            </Button>
          ), programsPagination, programs)}
        <DataTable
          columns={buildTableColumns([
            { field: 'name', header: 'Program' },
            { field: 'issuer_name', header: 'Issuer' },
            { field: 'investment_types', header: 'Types', render: (v) => (
              Array.isArray(v) && v.length
                ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {v.map((t) => <Chip key={t.id} label={t.reference} size="small" />)}
                  </Box>
                : '—'
            ) },
            { field: 'currency_code', header: 'Currency' },
            { field: 'limit_amount', header: 'Limit', render: (v) => (v === null || v === undefined ? 'No limit' : formatProgramAmount(v)) },
            { field: 'used_capital', header: 'Used', render: (v) => formatProgramAmount(v) },
            { field: 'available', header: 'Available', render: (v, row) => (row.limit_amount === null || row.limit_amount === undefined ? '—' : formatProgramAmount(v)) },
            { field: 'expiry_date', header: 'Expires', render: (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—') },
            { field: 'is_diminishing', header: 'Mode', render: (v) => (v ? <Chip label="Diminishing" color="warning" size="small" /> : <Chip label="Re-usable" size="small" />) },
            { field: 'listed', header: 'Listed', render: (v) => (v ? <Chip label="Listed" color="primary" size="small" /> : <Chip label="Unlisted" size="small" />) },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'programs')}
          data={orderInactiveFirst('programs', programs)}
          pagination={programsPagination}
          sort={programsSort}
          emptyMessage="No programs found"
        />
      </Box>
      )}

      {/* Interest Rates */}
      {activeFeature === 'interest_rates' && (
      <Box>
        {renderTabHeader('interestRates',
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => navigate('/rate-changes/impact')}
            >
              Impact Assessment
            </Button>
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('interestRates')}
              >
                Add Interest Rate
              </Button>
            )}
          </Box>, null, interestRates)}
        {/* Country sub-tabs and interest rates grouped by type */}
        {(() => {
          // Derive unique countries that have interest rates
          const countriesWithRates = [];
          const seenCountryIds = new Set();
          interestRates.forEach(rate => {
            if (rate.country_id && !seenCountryIds.has(rate.country_id)) {
              seenCountryIds.add(rate.country_id);
              countriesWithRates.push({
                country_id: rate.country_id,
                country_name: rate.country_name,
                currency_code: rate.currency_code,
              });
            }
          });
          countriesWithRates.sort((a, b) => (a.country_name || '').localeCompare(b.country_name || ''));

          // Filter rates by selected country sub-tab (0 = All Countries)
          const filteredRates = interestRateCountryTab === 0
            ? interestRates
            : interestRates.filter(r => r.country_id === countriesWithRates[interestRateCountryTab - 1]?.country_id);

          // Group filtered rates by interest_rate_type
          const groupedRates = filteredRates.reduce((acc, rate) => {
            const type = rate.interest_rate_type || 'Unknown';
            if (!acc[type]) acc[type] = [];
            acc[type].push(rate);
            return acc;
          }, {});

          // Sort each group by effective_date descending (most recent first)
          Object.keys(groupedRates).forEach(type => {
            groupedRates[type].sort((a, b) =>
              new Date(b.effective_date) - new Date(a.effective_date)
            );
          });

          return (
            <>
              {/* Country sub-tabs */}
              {countriesWithRates.length > 0 && (
                <Paper sx={{ mb: 3 }}>
                  <Tabs
                    value={interestRateCountryTab}
                    onChange={(e, v) => setInterestRateCountryTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="All Countries" {...a11yProps(0, 'ir-country')} />
                    {countriesWithRates.map((country, index) => (
                      <Tab
                        key={country.country_id}
                        label={`${country.country_name} (${country.currency_code})`}
                        {...a11yProps(index + 1, 'ir-country')}
                      />
                    ))}
                  </Tabs>
                </Paper>
              )}

              {/* Rate cards grouped by type */}
              {Object.keys(groupedRates).length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">No interest rates found</Typography>
                </Paper>
              ) : (
                Object.entries(groupedRates).map(([rateType, rates]) => {
                  const latestRate = rates[0]; // First one is the most recent
                  return (
                    <Card key={rateType} sx={{ mb: 3 }}>
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" component="span">
                              {rateType}
                            </Typography>
                            <Chip
                              label={`Current: ${latestRate.effective_interest_rate}%`}
                              color="primary"
                              size="small"
                              sx={{ ml: 2, fontWeight: 'bold' }}
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {rates.length} historical rate{rates.length !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </CardContent>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell>Effective Date</TableCell>
                              <TableCell align="right">Rate %</TableCell>
                              <TableCell>Institution</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="center">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rates.map((rate, index) => (
                              <TableRow
                                key={rate.id}
                                sx={{
                                  backgroundColor: index === 0 ? '#e3f2fd' : 'inherit',
                                  '&:hover': { backgroundColor: index === 0 ? '#bbdefb' : '#f5f5f5' }
                                }}
                              >
                                <TableCell>
                                  {rate.effective_date ? new Date(rate.effective_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  }) : '-'}
                                  {index === 0 && (
                                    <Chip label="Current" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontWeight={index === 0 ? 'bold' : 'normal'}>
                                    {rate.effective_interest_rate}%
                                  </Typography>
                                </TableCell>
                                <TableCell>{rate.prime_rate_institution || '-'}</TableCell>
                                <TableCell>{renderActiveChip(rate.active)}</TableCell>
                                <TableCell align="center">
                                  {canWrite && index === 0 && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenDialog('interestRates', rate)}
                                      title="Edit current rate"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {canAdmin && (
                                    rate.active === false ? (
                                      <IconButton
                                        size="small"
                                        color="success"
                                        title="Reactivate"
                                        onClick={() => handleReactivate('interestRates', rate)}
                                      >
                                        <RestoreIcon fontSize="small" />
                                      </IconButton>
                                    ) : (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        title="Delete"
                                        onClick={() => handleDelete('interestRates', rate.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  );
                })
              )}
            </>
          );
        })()}
      </Box>
      )}

      {/* FX Rates */}
      {activeFeature === 'fx_rates' && (
      <Box>
        {renderTabHeader('fxRates',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('fxRates')}
            >
              Add FX Rate
            </Button>
          ), fxRatesPagination, fxRates)}
        <DataTable
          columns={buildTableColumns([
            { field: 'currency_pair', header: 'Currency Pair' },
            { field: 'rate', header: 'Rate' },
            { field: 'effective_date', header: 'Effective Date' },
            { field: 'source', header: 'Source' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'fxRates')}
          data={orderInactiveFirst('fxRates', fxRates)}
          pagination={fxRatesPagination}
          sort={fxRatesSort}
          emptyMessage="No FX rates found"
        />
      </Box>
      )}

      {/* Placement Fees */}
      {activeFeature === 'placement_fees' && (
      <Box>
        {renderTabHeader('placementFees',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('placementFees')}
            >
              Add Placement Fee
            </Button>
          ), placementFeesPagination, placementFees)}
        <DataTable
          columns={buildTableColumns([
            { field: 'reference', header: 'Reference' },
            { field: 'type', header: 'Type' },
            { field: 'percentage', header: 'Percentage %', render: (v) => v != null ? `${formatRatePct(v)}%` : '-' },
            { field: 'applicable_products', header: 'Products' },
            { field: 'dr_expense_account', header: 'Expense Account' },
            { field: 'cr_control_account', header: 'Control Account' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'placementFees')}
          data={orderInactiveFirst('placementFees', placementFees)}
          pagination={placementFeesPagination}
          sort={placementFeesSort}
          emptyMessage="No placement fees found"
        />
      </Box>
      )}

      {/* Collateral Types */}
      {activeFeature === 'collateral_types' && (
      <Box>
        {renderTabHeader('collateralTypes',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('collateralTypes')}
            >
              Add Collateral Type
            </Button>
          ), collateralTypesPagination, collateralTypes)}
        <DataTable
          columns={buildTableColumns([
            { field: 'type', header: 'Type' },
            { field: 'description', header: 'Description' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'collateralTypes')}
          data={orderInactiveFirst('collateralTypes', collateralTypes)}
          pagination={collateralTypesPagination}
          sort={collateralTypesSort}
          emptyMessage="No collateral types found"
        />
      </Box>
      )}

      {/* Narrations */}
      {activeFeature === 'narrations' && (
      <Box>
        {renderTabHeader('narrations',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('narrations')}
            >
              Add Narration
            </Button>
          ), narrationsPagination, narrations)}
        <DataTable
          columns={buildTableColumns([
            { field: 'narration', header: 'Narration' },
            { field: 'classification', header: 'Classification' },
            { field: 'multiplier', header: 'Multiplier' },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'narrations')}
          data={orderInactiveFirst('narrations', narrations)}
          pagination={narrationsPagination}
          sort={narrationsSort}
          emptyMessage="No narrations found"
        />
      </Box>
      )}

      {/* WHT Rules */}
      {activeFeature === 'wht_rules' && (
      <Box>
        {renderTabHeader('whtRules',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('whtRules')}
            >
              Add WHT Rule
            </Button>
          ), whtRulesPagination, whtRules)}
        <DataTable
          columns={buildTableColumns([
            {
              field: 'tax_classification',
              header: 'Tax Classification',
              render: (val) => <Chip label={val} size="small" variant="outlined" />,
            },
            {
              field: 'country_name',
              header: 'Country',
              render: (val) => val || 'All Countries',
            },
            {
              field: 'wht_rate',
              header: 'WHT Rate',
              render: (val) => val != null ? `${val}%` : '-',
            },
            { field: 'description', header: 'Description' },
            {
              field: 'effective_from',
              header: 'Effective From',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
            },
            {
              field: 'effective_to',
              header: 'Effective To',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'No End Date',
            },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'whtRules')}
          data={orderInactiveFirst('whtRules', whtRules)}
          pagination={whtRulesPagination}
          sort={whtRulesSort}
          emptyMessage="No WHT rules found"
        />
      </Box>
      )}

      {/* Commission Rates */}
      {activeFeature === 'commission_rates' && (
      <Box>
        {renderTabHeader('commissionRates',
          canWrite && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('commissionRates')}
            >
              Add Commission Rate
            </Button>
          ), commissionRatesPagination, commissionRates)}
        <DataTable
          columns={buildTableColumns([
            {
              field: 'broker_name',
              header: 'Broker',
              render: (val) => val || 'All Brokers',
            },
            {
              field: 'investor_name',
              header: 'Investor',
              render: (val) => val || 'All Investors',
            },
            {
              field: 'rate_type',
              header: 'Rate Type',
              render: (val) => {
                const colorMap = { spread: 'primary', flat_fee: 'secondary', tiered: 'warning' };
                return <Chip label={val} size="small" color={colorMap[val] || 'default'} />;
              },
            },
            {
              field: 'rate',
              header: 'Rate',
              render: (val) => val != null ? `${val}%` : '-',
            },
            {
              field: 'min_amount',
              header: 'Min Amount',
              render: (val) => val != null ? formatNumber(Number(val), 2) : '-',
            },
            {
              field: 'max_amount',
              header: 'Max Amount',
              render: (val) => val != null ? formatNumber(Number(val), 2) : '-',
            },
            {
              field: 'effective_from',
              header: 'Effective From',
              render: (val) => val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
            },
            { field: 'active', header: 'Status', render: renderActiveChip },
          ], 'commissionRates')}
          data={orderInactiveFirst('commissionRates', commissionRates)}
          pagination={commissionRatesPagination}
          sort={commissionRatesSort}
          emptyMessage="No commission rates found"
        />
      </Box>
      )}

      {/* Generic Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit' : 'Add'} Reference Data
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Countries Form */}
              {dataType === 'countries' && (
                <>
                  <Controller
                    name="country"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Country"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="country_code"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country code is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Country Code"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="currency_code"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Currency code is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Currency Code"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="currency_symbol"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Currency Symbol"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Frequencies Form */}
              {dataType === 'frequencies' && (
                <>
                  <Controller
                    name="frequency"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Frequency is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Frequency"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="months_value"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Months value is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Months Value"
                        type="number"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Investment Types Form */}
              {dataType === 'investmentTypes' && (
                <>
                  <Controller
                    name="reference"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Reference is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Reference"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Type"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="category"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Category"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="cr_control_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Control Account"
                        fullWidth
                        placeholder="e.g., 2100-001"
                      />
                    )}
                  />
                  <Controller
                    name="dr_expense_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expense Account"
                        fullWidth
                        placeholder="e.g., 5100-001"
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Programs Form (capped MTN programs) */}
              {dataType === 'programs' && (
                <>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Program name is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Program Name"
                        fullWidth
                        required
                        placeholder="e.g., Select E1bn Listed MTN"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="issuer_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Issuer is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Issuer</InputLabel>
                        <Select {...field} label="Issuer" value={field.value || ''}>
                          {issuersList.map((iss) => (
                            <MenuItem key={iss.id} value={iss.id}>{iss.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="investment_type_ids"
                    control={control}
                    defaultValue={[]}
                    rules={{ validate: (v) => (Array.isArray(v) && v.length > 0) || 'At least one investment type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Investment Types</InputLabel>
                        <Select
                          {...field}
                          multiple
                          label="Investment Types"
                          value={Array.isArray(field.value) ? field.value : []}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((id) => {
                                const t = investmentTypes.find((x) => x.id === id);
                                return <Chip key={id} label={t ? t.reference : id} size="small" />;
                              })}
                            </Box>
                          )}
                        >
                          {investmentTypes.filter(t => t.active !== false).map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.reference} — {t.type}</MenuItem>
                          ))}
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="currency_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Currency is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency" value={field.value || ''}>
                          {countries.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.currency_code} ({c.country})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="limit_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Limit Amount"
                        type="number"
                        fullWidth
                        placeholder="Leave blank for no limit"
                        helperText="Maximum committed capital under this program. Blank = no limit."
                      />
                    )}
                  />
                  <Controller
                    name="expiry_date"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value || ''}
                        label="Expiry Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        helperText="Optional — after this date no new contracts can be added and existing contracts cannot have their capital increased."
                      />
                    )}
                  />
                  <Controller
                    name="is_diminishing"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Diminishing"
                      />
                    )}
                  />
                  <Controller
                    name="listed"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={!!field.value} />}
                        label="Listed"
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Interest Rates Form */}
              {dataType === 'interestRates' && (
                <>
                  <Controller
                    name="country_id"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Country is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        select
                        label="Country"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.country} ({country.currency_code})
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <Controller
                    name="interest_rate_type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        value={field.value || ''}
                        freeSolo
                        options={[...new Set(
                          interestRates
                            .filter(r => !watchedCountryId || r.country_id === watchedCountryId)
                            .map(r => r.interest_rate_type)
                        )].filter(Boolean)}
                        onChange={(_, value) => field.onChange(value || '')}
                        onInputChange={(_, value) => field.onChange(value || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Interest Rate Type"
                            required
                            error={!!error}
                            helperText={error?.message || 'Select existing or type new (e.g., Prime, TBOI, FNB)'}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="effective_interest_rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Interest Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="effective_date"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Date"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="prime_rate_institution"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Prime Rate Institution"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* FX Rates Form */}
              {dataType === 'fxRates' && (
                <>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Controller
                      name="from_currency"
                      control={control}
                      defaultValue=""
                      rules={{ required: 'From currency is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth required error={!!error}>
                          <InputLabel>From Currency</InputLabel>
                          <Select {...field} label="From Currency">
                            {[...new Set(countries.map(c => c.currency_code))].filter(Boolean).sort().map(code => (
                              <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                          </Select>
                          {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                        </FormControl>
                      )}
                    />
                    <Typography variant="h6">/</Typography>
                    <Controller
                      name="to_currency"
                      control={control}
                      defaultValue=""
                      rules={{ required: 'To currency is required' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth required error={!!error}>
                          <InputLabel>To Currency</InputLabel>
                          <Select {...field} label="To Currency">
                            {[...new Set(countries.map(c => c.currency_code))].filter(Boolean).sort().map(code => (
                              <MenuItem key={code} value={code}>{code}</MenuItem>
                            ))}
                          </Select>
                          {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                        </FormControl>
                      )}
                    />
                  </Box>
                  <Controller
                    name="rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Exchange Rate"
                        type="number"
                        inputProps={{ step: '0.0001' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="effective_date"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective Date"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="source"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Source"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Placement Fees Form */}
              {dataType === 'placementFees' && (
                <>
                  <Controller
                    name="reference"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Reference is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Reference"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        value={field.value || ''}
                        freeSolo
                        options={[...new Set(
                          (placementFees || []).map(f => f.type)
                        )].filter(Boolean)}
                        onChange={(_, value) => field.onChange(value || '')}
                        onInputChange={(_, value) => field.onChange(value || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Type"
                            required
                            error={!!error}
                            helperText={error?.message || "Select existing (e.g. Broker Fee, Stamp Duty) or type a new one"}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="percentage"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Percentage is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Percentage (%)"
                        type="number"
                        inputProps={{ step: '0.0001' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="dr_expense_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Expense Account (DR)"
                        fullWidth
                        helperText="GL debit account — used for journal export of this fee / duty"
                      />
                    )}
                  />
                  <Controller
                    name="cr_control_account"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Control Account (CR)"
                        fullWidth
                        helperText="GL credit account — used for journal export of this fee / duty"
                      />
                    )}
                  />
                  <Controller
                    name="applicable_products"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        options={investmentTypes.filter(t => t.active !== false)}
                        getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.type || '')}
                        value={
                          field.value
                            ? field.value.split(',').map(v => v.trim()).filter(Boolean).map(typeName =>
                                investmentTypes.find(t => t.type === typeName) || { type: typeName }
                              )
                            : []
                        }
                        onChange={(_, newValue) => {
                          field.onChange(newValue.map(v => v.type).join(','));
                        }}
                        isOptionEqualToValue={(option, value) => option.type === value.type}
                        renderInput={(params) => (
                          <TextField {...params} label="Applicable Products" fullWidth />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Collateral Types Form */}
              {dataType === 'collateralTypes' && (
                <>
                  <Controller
                    name="type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Type"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Narrations Form */}
              {dataType === 'narrations' && (
                <>
                  <Controller
                    name="narration"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Narration is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Narration"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        multiline
                        rows={2}
                      />
                    )}
                  />
                  <Controller
                    name="classification"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Classification"
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="multiplier"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Multiplier"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* WHT Rules Form */}
              {dataType === 'whtRules' && (
                <>
                  <Controller
                    name="tax_classification"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Tax classification is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Tax Classification</InputLabel>
                        <Select {...field} label="Tax Classification">
                          {['Individual', 'Corporate', 'Foreign', 'Trust', 'Exempt'].map((opt) => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="country_id"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <Autocomplete
                        options={countries}
                        getOptionLabel={(opt) => opt.country ? `${opt.country} (${opt.currency_code})` : ''}
                        value={countries.find((c) => c.id === field.value) || null}
                        onChange={(_, val) => field.onChange(val ? val.id : null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Country (leave blank for all countries)"
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="wht_rate"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: 'WHT rate is required',
                      min: { value: 0, message: 'Rate must be at least 0' },
                      max: { value: 100, message: 'Rate cannot exceed 100' },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="WHT Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01', min: 0, max: 100 }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  />
                  <Controller
                    name="effective_from"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective from date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective From"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="effective_to"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Effective To (optional)"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}

              {/* Commission Rates Form */}
              {dataType === 'commissionRates' && (
                <>
                  <Controller
                    name="broker_id"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Autocomplete
                        {...field}
                        options={brokersList}
                        getOptionLabel={(option) => option.name || ''}
                        value={value ? brokersList.find((b) => b.id === value) || null : null}
                        onChange={(_, data) => onChange(data?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Broker (optional)"
                            fullWidth
                            helperText="Leave blank to apply to all brokers"
                          />
                        )}
                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                      />
                    )}
                  />
                  <Controller
                    name="investor_id"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Autocomplete
                        {...field}
                        options={investorsList}
                        getOptionLabel={(option) => option.name || ''}
                        value={value ? investorsList.find((i) => i.id === value) || null : null}
                        onChange={(_, data) => onChange(data?.id ?? null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Investor (optional)"
                            fullWidth
                            helperText="Leave blank to apply to all investors"
                          />
                        )}
                        isOptionEqualToValue={(option, val) => option.id === val?.id}
                      />
                    )}
                  />
                  <Controller
                    name="rate_type"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate type is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth required error={!!error}>
                        <InputLabel>Rate Type</InputLabel>
                        <Select {...field} label="Rate Type">
                          <MenuItem value="spread">Spread</MenuItem>
                          <MenuItem value="flat_fee">Flat Fee</MenuItem>
                          <MenuItem value="tiered">Tiered</MenuItem>
                        </Select>
                        {error && <Typography variant="caption" color="error">{error.message}</Typography>}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="rate"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Rate is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Rate (%)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name="min_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Min Amount (optional)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  <Controller
                    name="max_amount"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max Amount (optional)"
                        type="number"
                        inputProps={{ step: '0.01' }}
                        fullWidth
                        onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  <Controller
                    name="effective_from"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Effective from date is required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Effective From"
                        type="date"
                        fullWidth
                        required
                        error={!!error}
                        helperText={error?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Active"
                      />
                    )}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Impact Assessment Prompt Dialog */}
      <Dialog
        open={impactDialog.open}
        onClose={() => setImpactDialog({ ...impactDialog, open: false })}
      >
        <DialogTitle>Run Impact Assessment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This interest rate change may impact variable-rate contracts linked to{' '}
            <strong>{impactDialog.rateType}</strong>.
            <br /><br />
            Would you like to run an impact assessment to see which contracts will be affected?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImpactDialog({ ...impactDialog, open: false })}>
            No, Thanks
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const params = new URLSearchParams({
                type: impactDialog.rateType,
                country: impactDialog.countryId,
                rate: impactDialog.newRate,
                date: impactDialog.effectiveDate,
                benchmark_id: impactDialog.benchmarkRateId,
              });
              navigate(`/rate-changes/impact?${params.toString()}`);
            }}
          >
            Run Impact Assessment
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
}

export default ReferenceDataManagement;
